/**
 * Results grid for the FP Monthly COGS Tracker.
 *
 * One row per FP. Left identity band stays sticky; month columns scroll
 * horizontally inside the TableContainer so the page never widens past
 * the viewport. Each month cell links to its workbook tab.
 */
import React from 'react'
import {
  Box,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { ExcelIcon, GoogleSheetsIcon } from '../../../../../components/SystemIcons'
import { getCountryChipSx } from '../../../../../utils/countryChips'
import {
  statusPillChipProps,
  type StatusTone,
} from '../../../../../components/shared/AppDrawer/chipPresets'
import type { CogsTrackerRow, CogsCellState } from './types'
import CogsCellPopover from './CogsCellPopover'
import CogsRowActionsMenu from './CogsRowActionsMenu'

interface CogsTrackerGridProps {
  rows: CogsTrackerRow[]
  windowMonths: string[]
}

const MONTH_LABEL: Record<CogsCellState, string> = {
  posted: 'Done',
  outstanding: 'Post',
  needs_review: 'Review',
  future: '-',
  none: '-',
}

const MONTH_COLOUR: Record<CogsCellState, string> = {
  posted: 'success.main',
  outstanding: 'warning.main',
  needs_review: 'error.main',
  future: 'text.disabled',
  none: 'text.disabled',
}

const STATE_SEVERITY: Record<CogsCellState, number> = {
  needs_review: 4,
  outstanding: 3,
  posted: 2,
  future: 1,
  none: 0,
}

const currentMonthKey = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const monthLabel = (key: string): string => {
  const [y, m] = key.split('-').map(Number)
  if (!y || !m) return key
  return new Date(y, m - 1, 1).toLocaleString(undefined, {
    month: 'short',
    year: '2-digit',
  })
}

const cogsToolPill = (
  toolSource: CogsTrackerRow['tool_source'],
): { label: string; tone: StatusTone; icon?: React.ReactElement } => {
  if (toolSource === 'google')
    return { label: 'Google', tone: 'success', icon: <GoogleSheetsIcon size={12} /> }
  if (toolSource === 'legacy_excel')
    return { label: 'Excel', tone: 'neutral', icon: <ExcelIcon size={12} /> }
  return { label: 'No tool', tone: 'error' }
}

const currentMonthPill = (
  row: CogsTrackerRow,
  monthKey: string,
): { label: string; tone: StatusTone } | null => {
  const cell = row.months?.[monthKey]
  if (!cell) {
    return row.tool_source === null
      ? { label: 'No tool', tone: 'error' }
      : null
  }
  switch (cell.state) {
    case 'posted':
      return { label: 'Done', tone: 'success' }
    case 'needs_review':
      return { label: 'Needs review', tone: 'error' }
    case 'outstanding':
      return { label: 'Ready to post', tone: 'warning' }
    case 'none':
      return row.tool_source === null
        ? { label: 'No tool', tone: 'error' }
        : null
    case 'future':
    default:
      return null
  }
}

const outstandingCount = (row: CogsTrackerRow): number => {
  if (!row.months) return 0
  return Object.values(row.months).filter(
    (cell) => cell.state === 'outstanding' || cell.state === 'needs_review',
  ).length
}

const outstandingColour = (count: number): string => {
  if (count === 0) return 'text.disabled'
  if (count <= 2) return 'warning.main'
  return 'error.main'
}

type SortKey =
  | 'franchise_name'
  | 'rom_name'
  | 'tool_source'
  | 'current_month'
  | 'outstanding'
  | `month:${string}`

interface SortState {
  key: SortKey
  dir: 'asc' | 'desc'
}

const sortValue = (
  row: CogsTrackerRow,
  key: SortKey,
  monthKey: string,
): number | string => {
  if (typeof key === 'string' && key.startsWith('month:')) {
    const m = key.slice('month:'.length)
    const cell = row.months?.[m]
    return cell ? STATE_SEVERITY[cell.state] : -1
  }
  if (key === 'current_month') {
    const cell = row.months?.[monthKey]
    return cell ? STATE_SEVERITY[cell.state] : -1
  }
  if (key === 'outstanding') return outstandingCount(row)
  const v = (row as unknown as Record<string, unknown>)[key as string]
  if (v === null || v === undefined) return ''
  if (typeof v === 'number') return v
  return String(v)
}

const GROUP_HEADER_BG = '#E0E0E0'
const COLUMN_HEADER_BG = '#F5F5F5'
const GROUP_DIVIDER = '2px solid #BDBDBD'
const CELL_DIVIDER = '1px solid #E0E0E0'

const GROUP_HEADER_HEIGHT = 32

const headerCellSx = {
  fontWeight: 600,
  fontSize: '0.7rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
  bgcolor: COLUMN_HEADER_BG,
  whiteSpace: 'nowrap',
  borderBottom: '2px solid',
  borderColor: '#BDBDBD',
  borderRight: CELL_DIVIDER,
  top: GROUP_HEADER_HEIGHT,
}

const groupHeaderCellSx = {
  ...headerCellSx,
  fontSize: '0.72rem',
  bgcolor: GROUP_HEADER_BG,
  color: 'text.primary',
  borderBottom: '1px solid #BDBDBD',
  borderRight: GROUP_DIVIDER,
  py: 0.5,
  top: 0,
  height: GROUP_HEADER_HEIGHT,
}

const FRANCHISE_COL_WIDTH = 280

const stickyLeftHeader = {
  ...headerCellSx,
  position: 'sticky' as const,
  left: 0,
  zIndex: 3,
  minWidth: FRANCHISE_COL_WIDTH,
  width: FRANCHISE_COL_WIDTH,
  borderRight: GROUP_DIVIDER,
}

const stickyLeftCell = {
  position: 'sticky' as const,
  left: 0,
  bgcolor: 'background.paper',
  zIndex: 1,
  minWidth: FRANCHISE_COL_WIDTH,
  width: FRANCHISE_COL_WIDTH,
  borderRight: GROUP_DIVIDER,
}

const bodyCellSx = { borderRight: CELL_DIVIDER }
const bodyGroupBoundarySx = { borderRight: GROUP_DIVIDER }

const CogsTrackerGrid: React.FC<CogsTrackerGridProps> = ({ rows, windowMonths }) => {
  const monthKey = currentMonthKey()
  const [sort, setSort] = React.useState<SortState>({
    key: 'franchise_name',
    dir: 'asc',
  })

  const sortedRows = React.useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = sortValue(a, sort.key, monthKey)
      const bv = sortValue(b, sort.key, monthKey)
      if (av === bv) return 0
      const cmp = av < bv ? -1 : 1
      return sort.dir === 'asc' ? cmp : -cmp
    })
    return copy
  }, [rows, sort, monthKey])

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    )
  }

  const ACTIONS_COL_WIDTH = 48
  const colCount = 6 + windowMonths.length + 1

  const stickyRightHeader = {
    ...headerCellSx,
    position: 'sticky' as const,
    right: 0,
    zIndex: 3,
    width: ACTIONS_COL_WIDTH,
    minWidth: ACTIONS_COL_WIDTH,
    borderRight: 'none',
    borderLeft: GROUP_DIVIDER,
  }
  const stickyRightGroupHeader = {
    ...groupHeaderCellSx,
    position: 'sticky' as const,
    right: 0,
    zIndex: 4,
    width: ACTIONS_COL_WIDTH,
    minWidth: ACTIONS_COL_WIDTH,
    borderRight: 'none',
    borderLeft: GROUP_DIVIDER,
  }
  const stickyRightCell = {
    position: 'sticky' as const,
    right: 0,
    bgcolor: 'background.paper',
    zIndex: 1,
    width: ACTIONS_COL_WIDTH,
    minWidth: ACTIONS_COL_WIDTH,
    borderLeft: GROUP_DIVIDER,
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        maxWidth: '100%',
        overflow: 'auto',
        maxHeight: 'calc(100vh - 380px)',
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                ...groupHeaderCellSx,
                position: 'sticky',
                left: 0,
                zIndex: 4,
              }}
              colSpan={2}
              align="center"
            >
              Franchise
            </TableCell>
            <TableCell sx={groupHeaderCellSx} colSpan={2} align="center">
              Tooling
            </TableCell>
            <TableCell sx={groupHeaderCellSx} colSpan={2} align="center">
              COGS summary
            </TableCell>
            {windowMonths.length > 0 ? (
              <TableCell
                sx={{ ...groupHeaderCellSx, borderRight: 'none' }}
                colSpan={windowMonths.length}
                align="center"
              >
                COGS monthly status
              </TableCell>
            ) : null}
            <TableCell sx={stickyRightGroupHeader} align="center"> </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={stickyLeftHeader}>
              <TableSortLabel
                active={sort.key === 'franchise_name'}
                direction={sort.dir}
                onClick={() => toggleSort('franchise_name')}
              >
                Franchise
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...headerCellSx, borderRight: GROUP_DIVIDER }}>
              <TableSortLabel
                active={sort.key === 'rom_name'}
                direction={sort.dir}
                onClick={() => toggleSort('rom_name')}
              >
                ROM / FBC
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="center"
              sx={{
                ...headerCellSx,
                whiteSpace: 'normal',
                lineHeight: 1.2,
                maxWidth: 120,
              }}
            >
              <TableSortLabel
                active={sort.key === 'tool_source'}
                direction={sort.dir}
                onClick={() => toggleSort('tool_source')}
              >
                Monthly<br />COGS Tool
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="center"
              sx={{
                ...headerCellSx,
                borderRight: GROUP_DIVIDER,
                whiteSpace: 'normal',
                lineHeight: 1.2,
                maxWidth: 120,
              }}
            >
              Product<br />Avg cost tool
            </TableCell>
            <TableCell
              sx={{
                ...headerCellSx,
                whiteSpace: 'normal',
                maxWidth: 90,
                lineHeight: 1.2,
              }}
              align="center"
            >
              <TableSortLabel
                active={sort.key === 'current_month'}
                direction={sort.dir}
                onClick={() => toggleSort('current_month')}
              >
                Current<br />month
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ ...headerCellSx, borderRight: GROUP_DIVIDER }}
              align="center"
            >
              <TableSortLabel
                active={sort.key === 'outstanding'}
                direction={sort.dir}
                onClick={() => toggleSort('outstanding')}
              >
                Outstanding
              </TableSortLabel>
            </TableCell>
            {windowMonths.map((m, idx) => (
              <TableCell
                key={m}
                sx={{
                  ...headerCellSx,
                  borderRight:
                    idx === windowMonths.length - 1 ? 'none' : CELL_DIVIDER,
                }}
                align="center"
              >
                <TableSortLabel
                  active={sort.key === `month:${m}`}
                  direction={sort.dir}
                  onClick={() => toggleSort(`month:${m}` as SortKey)}
                >
                  {monthLabel(m)}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell sx={stickyRightHeader} align="center">
              &nbsp;
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row) => {
            const toolPill = cogsToolPill(row.tool_source)
            const currentPill = currentMonthPill(row, monthKey)
            const outstanding = outstandingCount(row)
            const regionLabel = row.region_name || row.region_code || null
            return (
              <TableRow key={row.fp_id} hover>
                <TableCell sx={stickyLeftCell}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={row.country}
                      size="small"
                      variant="outlined"
                      sx={getCountryChipSx(row.country)}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, lineHeight: 1.2 }}
                      >
                        {row.franchise_name}
                      </Typography>
                      {regionLabel ? (
                        <Typography variant="caption" color="text.secondary">
                          {regionLabel}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', ...bodyGroupBoundarySx }}>
                  <Typography variant="body2">{row.rom_name || '-'}</Typography>
                </TableCell>
                <TableCell align="center" sx={bodyCellSx}>
                  <Chip
                    {...statusPillChipProps(toolPill.label, toolPill.tone)}
                    icon={toolPill.icon}
                  />
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ whiteSpace: 'nowrap', ...bodyGroupBoundarySx }}
                >
                  {row.pac_sheet_url ? (
                    <Link
                      href={row.pac_sheet_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        fontSize: '0.8125rem',
                      }}
                    >
                      Open sheet
                      <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      -
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', lineHeight: 1.2 }}
                  >
                    {row.pac_evo_status || 'Not found'}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={bodyCellSx}>
                  {currentPill ? (
                    <Chip
                      {...statusPillChipProps(currentPill.label, currentPill.tone)}
                    />
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={bodyGroupBoundarySx}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: outstandingColour(outstanding),
                    }}
                  >
                    {outstanding}
                  </Typography>
                </TableCell>
                {windowMonths.map((m, idx) => {
                  const cell = row.months?.[m]
                  const state: CogsCellState = cell ? cell.state : 'none'
                  const url = cell?.sheet_url
                  const label = MONTH_LABEL[state]
                  const colour = MONTH_COLOUR[state]
                  const content = (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: colour,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {label}
                    </Typography>
                  )
                  const inner = url ? (
                    <Link
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                    >
                      {content}
                    </Link>
                  ) : (
                    <Box>{content}</Box>
                  )
                  return (
                    <TableCell
                      key={m}
                      align="center"
                      sx={{
                        px: 1,
                        borderRight:
                          idx === windowMonths.length - 1 ? 'none' : CELL_DIVIDER,
                      }}
                    >
                      <CogsCellPopover monthLabel={monthLabel(m)} cell={cell}>
                        {inner}
                      </CogsCellPopover>
                    </TableCell>
                  )
                })}
                <TableCell sx={stickyRightCell} align="center">
                  <CogsRowActionsMenu
                    fpCode={row.fp_code}
                    fpId={row.fp_id}
                    cogsSheetUrl={row.cogs_sheet_url}
                    pacSheetUrl={row.pac_sheet_url}
                  />
                </TableCell>
              </TableRow>
            )
          })}
          {sortedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colCount} align="center">
                <Box sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No franchises match these filters.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default CogsTrackerGrid
